/**
 * Utility for persisting grid sorting preferences to localStorage
 */

import { useState } from 'react'

export interface SortingPreference {
  sortBy: string
  order: 'asc' | 'desc'
}

export interface PaginationPreference {
  pageSize: number
  pageIndex: number
}

export interface DateRangeFilter {
  startDate: string | null
  endDate: string | null
}

export interface GridFilters {
  [key: string]: DateRangeFilter | any
}

export type MRT_SortingState = Array<{ id: string; desc: boolean }>
export type SortingUpdater =
  | MRT_SortingState
  | ((old: MRT_SortingState) => MRT_SortingState)

export type MRT_ColumnFiltersState = Array<{ id: string; value: any }>
export type ColumnFiltersUpdater =
  | MRT_ColumnFiltersState
  | ((old: MRT_ColumnFiltersState) => MRT_ColumnFiltersState)

export type MRT_PaginationState = { pageSize: number; pageIndex: number }
export type PaginationUpdater =
  | MRT_PaginationState
  | ((old: MRT_PaginationState) => MRT_PaginationState)

export type MRT_ColumnVisibilityState = Record<string, boolean>
export type ColumnVisibilityUpdater =
  | MRT_ColumnVisibilityState
  | ((old: MRT_ColumnVisibilityState) => MRT_ColumnVisibilityState)

/**
 * Create localStorage helpers for a specific grid
 * @param storageKey - Unique key for this grid's sorting preferences
 * @param defaultSortBy - Default column to sort by
 * @param defaultOrder - Default sort order ('asc' or 'desc')
 * @returns Object with load and save functions
 */
export function createGridSortingStorage(
  storageKey: string,
  defaultSortBy: string = 'name',
  defaultOrder: 'asc' | 'desc' = 'asc',
) {
  /**
   * Load sorting preferences from localStorage
   */
  function loadSortFromStorage(): SortingPreference {
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (error) {
      console.error(
        `Error loading sorting preferences for ${storageKey}:`,
        error,
      )
    }
    return { sortBy: defaultSortBy, order: defaultOrder }
  }

  /**
   * Save sorting preferences to localStorage
   */
  function saveSortToStorage(sortBy: string, order: 'asc' | 'desc'): void {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ sortBy, order }))
    } catch (error) {
      console.error(
        `Error saving sorting preferences for ${storageKey}:`,
        error,
      )
    }
  }

  /**
   * Clear sorting preferences from localStorage
   */
  function clearSortFromStorage(): void {
    try {
      localStorage.removeItem(storageKey)
    } catch (error) {
      console.error(
        `Error clearing sorting preferences for ${storageKey}:`,
        error,
      )
    }
  }

  return {
    loadSortFromStorage,
    saveSortToStorage,
    clearSortFromStorage,
  }
}

/**
 * Pre-configured storage helpers for common grids
 */
export const GridSortingStorage = {
  deals: createGridSortingStorage('deal-grid-sort', 'dealName', 'asc'),
  lockedDeals: createGridSortingStorage(
    'locked-deals-grid-sort',
    'dealName',
    'asc',
  ),
  facilities: createGridSortingStorage('facility-grid-sort', 'dealName', 'asc'),
  lockedFacilities: createGridSortingStorage(
    'locked-facilities-grid-sort',
    'dealName',
    'asc',
  ),
  facilityLocs: createGridSortingStorage(
    'facility-locs-grid-sort',
    'locId',
    'asc',
  ),
  facilityLoans: createGridSortingStorage(
    'facility-loans-grid-sort',
    'loanId',
    'asc',
  ),
  facilityHistory: createGridSortingStorage(
    'facility-history-grid-sort',
    'processedAt',
    'asc',
  ),
  myworkScheduled: createGridSortingStorage(
    'mywork-scheduled-grid-sort',
    'effectiveDate',
    'asc',
  ),
  myworkTodo: createGridSortingStorage(
    'mywork-todo-grid-sort',
    'timeDue',
    'asc',
  ),
  myworkUnscheduled: createGridSortingStorage(
    'mywork-unscheduled-grid-sort',
    'effectiveDate',
    'asc',
  ),
  notices: createGridSortingStorage(
    'notices-grid-sort',
    'effectiveDate',
    'asc',
  ),
  payments: createGridSortingStorage(
    'payments-grid-sort',
    'effectiveDate',
    'asc',
  ),
  tradeClosing: createGridSortingStorage(
    'trade-closing-grid-sort',
    'effectiveDate',
    'asc',
  ),
  tradeEntry: createGridSortingStorage(
    'trade-entry-grid-sort',
    'tradeDate',
    'asc',
  ),
  documentTradeEntry: createGridSortingStorage(
    'document-trade-entry-grid-sort',
    'systemId',
    'asc',
  ),
  subTradeEntry: createGridSortingStorage(
    'sub-trade-entry-grid-sort',
    'systemId',
    'asc',
  ),
  entities: createGridSortingStorage('entity-grid-sort', 'entityName', 'asc'),
  entityContacts: createGridSortingStorage(
    'entity-contacts-grid-sort',
    'name',
    'asc',
  ),
  entityPositions: createGridSortingStorage(
    'entity-positions-grid-sort',
    'deal',
    'asc',
  ),
  entityWireInstructions: createGridSortingStorage(
    'entity-wire-instructions-grid-sort',
    'entityName',
    'asc',
  ),
  servicing: createGridSortingStorage(
    'servicing-grid-sort',
    'effectiveDate',
    'asc',
  ),
  amortizationSchedule: createGridSortingStorage(
    'amortization-schedule-grid-sort',
    'paymentDueDate',
    'asc',
  ),
  facilityFees: createGridSortingStorage(
    'facility-fees-grid-sort',
    'startDate',
    'asc',
  ),
  positionsDeal: createGridSortingStorage(
    'positions-deal-grid-sort',
    'dealName',
    'asc',
  ),
  positionsFacility: createGridSortingStorage(
    'positions-facility-grid-sort',
    'facilityName',
    'asc',
  ),
  positionsEntity: createGridSortingStorage(
    'positions-entity-grid-sort',
    'entityName',
    'asc',
  ),
}

/**
 * Create localStorage helpers for grid pagination (page size, page index)
 * @param storageKey - Unique key for this grid's pagination preferences
 * @param defaultPageSize - Default page size (default: 25)
 * @param defaultPageIndex - Default page index (default: 0)
 * @returns Object with load, save, and clear functions
 */
export function createGridPaginationStorage(
  storageKey: string,
  defaultPageSize: number = 25,
  defaultPageIndex: number = 0,
) {
  /**
   * Load pagination preferences from localStorage
   */
  function loadPaginationFromStorage(): PaginationPreference {
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (error) {
      console.error(
        `Error loading pagination preferences for ${storageKey}:`,
        error,
      )
    }
    return { pageSize: defaultPageSize, pageIndex: defaultPageIndex }
  }

  /**
   * Save pagination preferences to localStorage
   */
  function savePaginationToStorage(pagination: PaginationPreference): void {
    try {
      localStorage.setItem(storageKey, JSON.stringify(pagination))
    } catch (error) {
      console.error(
        `Error saving pagination preferences for ${storageKey}:`,
        error,
      )
    }
  }

  /**
   * Clear pagination preferences from localStorage
   */
  function clearPaginationFromStorage(): void {
    try {
      localStorage.removeItem(storageKey)
    } catch (error) {
      console.error(
        `Error clearing pagination preferences for ${storageKey}:`,
        error,
      )
    }
  }

  return {
    loadPaginationFromStorage,
    savePaginationToStorage,
    clearPaginationFromStorage,
  }
}

/**
 * Pre-configured pagination storage helpers for common grids
 */
export const GridPaginationStorage = {
  deals: createGridPaginationStorage('deal-grid-pagination', 25, 0),
  lockedDeals: createGridPaginationStorage(
    'locked-deals-grid-pagination',
    25,
    0,
  ),
  facilities: createGridPaginationStorage('facility-grid-pagination', 25, 0),
  lockedFacilities: createGridPaginationStorage(
    'locked-facilities-grid-pagination',
    25,
    0,
  ),
  facilityLocs: createGridPaginationStorage(
    'facility-locs-grid-pagination',
    5,
    0,
  ),
  facilityLoans: createGridPaginationStorage(
    'facility-loans-grid-pagination',
    5,
    0,
  ),
  facilityHistory: createGridPaginationStorage(
    'facility-history-grid-pagination',
    25,
    0,
  ),
  myworkScheduled: createGridPaginationStorage(
    'mywork-scheduled-grid-pagination',
    25,
    0,
  ),
  myworkTodo: createGridPaginationStorage('mywork-todo-grid-pagination', 25, 0),
  myworkUnscheduled: createGridPaginationStorage(
    'mywork-unscheduled-grid-pagination',
    25,
    0,
  ),
  notices: createGridPaginationStorage('notices-grid-pagination', 25, 0),
  payments: createGridPaginationStorage('payments-grid-pagination', 25, 0),
  tradeClosing: createGridPaginationStorage(
    'trade-closing-grid-pagination',
    25,
    0,
  ),
  tradeEntry: createGridPaginationStorage('trade-entry-grid-pagination', 25, 0),
  documentTradeEntry: createGridPaginationStorage(
    'document-trade-entry-grid-pagination',
    25,
    0,
  ),
  subTradeEntry: createGridPaginationStorage(
    'sub-trade-entry-grid-pagination',
    25,
    0,
  ),
  entities: createGridPaginationStorage('entities-grid-pagination', 25, 0),
  entityContacts: createGridPaginationStorage(
    'entity-contacts-grid-pagination',
    5,
    0,
  ),
  entityPositions: createGridPaginationStorage(
    'entity-positions-grid-pagination',
    5,
    0,
  ),
  entityWireInstructions: createGridPaginationStorage(
    'entity-wire-instructions-grid-pagination',
    5,
    0,
  ),
  servicing: createGridPaginationStorage('servicing-grid-pagination', 25, 0),
  amortizationSchedule: createGridPaginationStorage(
    'amortization-schedule-grid-pagination',
    5,
    0,
  ),
  facilityFees: createGridPaginationStorage(
    'facility-fees-grid-pagination',
    5,
    0,
  ),
  positionsDeal: createGridPaginationStorage(
    'positions-deal-grid-pagination',
    25,
    0,
  ),
  positionsFacility: createGridPaginationStorage(
    'positions-facility-grid-pagination',
    25,
    0,
  ),
  positionsEntity: createGridPaginationStorage(
    'positions-entity-grid-pagination',
    25,
    0,
  ),
}

/**
 * Create localStorage helpers for grid column visibility
 * @param storageKey - Unique key for this grid's column visibility preferences
 * @returns Object with load, save, and clear functions
 */
export function createGridColumnVisibilityStorage(storageKey: string) {
  /**
   * Load column visibility preferences from localStorage
   */
  function loadColumnVisibilityFromStorage(): MRT_ColumnVisibilityState {
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (error) {
      console.error(
        `Error loading column visibility preferences for ${storageKey}:`,
        error,
      )
    }
    return {}
  }

  /**
   * Save column visibility preferences to localStorage
   */
  function saveColumnVisibilityToStorage(
    visibility: MRT_ColumnVisibilityState,
  ): void {
    try {
      localStorage.setItem(storageKey, JSON.stringify(visibility))
    } catch (error) {
      console.error(
        `Error saving column visibility preferences for ${storageKey}:`,
        error,
      )
    }
  }

  /**
   * Clear column visibility preferences from localStorage
   */
  function clearColumnVisibilityFromStorage(): void {
    try {
      localStorage.removeItem(storageKey)
    } catch (error) {
      console.error(
        `Error clearing column visibility preferences for ${storageKey}:`,
        error,
      )
    }
  }

  return {
    loadColumnVisibilityFromStorage,
    saveColumnVisibilityToStorage,
    clearColumnVisibilityFromStorage,
  }
}

/**
 * Pre-configured column visibility storage helpers for common grids
 */
export const GridColumnVisibilityStorage = {
  deals: createGridColumnVisibilityStorage('deal-grid-column-visibility'),
  lockedDeals: createGridColumnVisibilityStorage(
    'locked-deals-grid-column-visibility',
  ),
  facilities: createGridColumnVisibilityStorage(
    'facility-grid-column-visibility',
  ),
  lockedFacilities: createGridColumnVisibilityStorage(
    'locked-facilities-grid-column-visibility',
  ),
  facilityLocs: createGridColumnVisibilityStorage(
    'facility-locs-grid-column-visibility',
  ),
  facilityLoans: createGridColumnVisibilityStorage(
    'facility-loans-grid-column-visibility',
  ),
  facilityHistory: createGridColumnVisibilityStorage(
    'facility-history-grid-column-visibility',
  ),
  myworkScheduled: createGridColumnVisibilityStorage(
    'mywork-scheduled-grid-column-visibility',
  ),
  myworkTodo: createGridColumnVisibilityStorage(
    'mywork-todo-grid-column-visibility',
  ),
  myworkUnscheduled: createGridColumnVisibilityStorage(
    'mywork-unscheduled-grid-column-visibility',
  ),
  notices: createGridColumnVisibilityStorage('notices-grid-column-visibility'),
  payments: createGridColumnVisibilityStorage(
    'payments-grid-column-visibility',
  ),
  tradeClosing: createGridColumnVisibilityStorage(
    'trade-closing-grid-column-visibility',
  ),
  tradeEntry: createGridColumnVisibilityStorage(
    'trade-entry-grid-column-visibility',
  ),
  documentTradeEntry: createGridColumnVisibilityStorage(
    'document-trade-entry-grid-column-visibility',
  ),
  subTradeEntry: createGridColumnVisibilityStorage(
    'sub-trade-entry-grid-column-visibility',
  ),
  entities: createGridColumnVisibilityStorage('entity-grid-column-visibility'),
  entityContacts: createGridColumnVisibilityStorage(
    'entity-contacts-grid-column-visibility',
  ),
  entityPositions: createGridColumnVisibilityStorage(
    'entity-positions-grid-column-visibility',
  ),
  entityWireInstructions: createGridColumnVisibilityStorage(
    'entity-wire-instructions-grid-column-visibility',
  ),
  servicing: createGridColumnVisibilityStorage(
    'servicing-grid-column-visibility',
  ),
  amortizationSchedule: createGridColumnVisibilityStorage(
    'amortization-schedule-grid-column-visibility',
  ),
  facilityFees: createGridColumnVisibilityStorage(
    'facility-fees-grid-column-visibility',
  ),
  positionsDeal: createGridColumnVisibilityStorage(
    'positions-deal-grid-column-visibility',
  ),
  positionsFacility: createGridColumnVisibilityStorage(
    'positions-facility-grid-column-visibility',
  ),
  positionsEntity: createGridColumnVisibilityStorage(
    'positions-entity-grid-column-visibility',
  ),
}

/**
 * Create localStorage helpers for grid filters (date ranges, etc.)
 * @param storageKey - Unique key for this grid's filter preferences
 * @returns Object with load, save, and clear functions
 */
export function createGridFilterStorage(storageKey: string) {
  /**
   * Load filter preferences from localStorage
   */
  function loadFiltersFromStorage(): GridFilters {
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        const parsed = JSON.parse(stored)
        // Convert ISO date strings back to Date objects
        Object.keys(parsed).forEach(key => {
          const value = parsed[key]
          if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
            parsed[key] = new Date(value)
          }
        })
        return parsed
      }
    } catch (error) {
      console.error(
        `Error loading filter preferences for ${storageKey}:`,
        error,
      )
    }
    return {}
  }

  /**
   * Save filter preferences to localStorage
   */
  function saveFiltersToStorage(filters: GridFilters): void {
    try {
      // Convert Date objects to ISO strings for storage
      const serializable = { ...filters }
      Object.keys(serializable).forEach(key => {
        const value = serializable[key]
        if (value instanceof Date) {
          serializable[key] = value.toISOString()
        }
      })
      localStorage.setItem(storageKey, JSON.stringify(serializable))
    } catch (error) {
      console.error(`Error saving filter preferences for ${storageKey}:`, error)
    }
  }

  /**
   * Clear filter preferences from localStorage
   */
  function clearFiltersFromStorage(): void {
    try {
      localStorage.removeItem(storageKey)
    } catch (error) {
      console.error(
        `Error clearing filter preferences for ${storageKey}:`,
        error,
      )
    }
  }

  return {
    loadFiltersFromStorage,
    saveFiltersToStorage,
    clearFiltersFromStorage,
  }
}

/**
 * Pre-configured filter storage helpers for common grids
 */
export const GridFilterStorage = {
  deals: createGridFilterStorage('deal-grid-filters'),
  lockedDeals: createGridFilterStorage('locked-deals-grid-filters'),
  facilities: createGridFilterStorage('facility-grid-filters'),
  lockedFacilities: createGridFilterStorage('locked-facilities-grid-filters'),
  facilityLocs: createGridFilterStorage('facility-locs-grid-filters'),
  facilityLoans: createGridFilterStorage('facility-loans-grid-filters'),
  facilityHistory: createGridFilterStorage('facility-history-grid-filters'),
  myworkScheduled: createGridFilterStorage('mywork-scheduled-grid-filters'),
  myworkTodo: createGridFilterStorage('mywork-todo-grid-filters'),
  myworkUnscheduled: createGridFilterStorage('mywork-unscheduled-grid-filters'),
  notices: createGridFilterStorage('notices-grid-filters'),
  payments: createGridFilterStorage('payments-grid-filters'),
  tradeClosing: createGridFilterStorage('trade-closing-grid-filters'),
  tradeEntry: createGridFilterStorage('trade-entry-grid-filters'),
  documentTradeEntry: createGridFilterStorage(
    'document-trade-entry-grid-filters',
  ),
  subTradeEntry: createGridFilterStorage('sub-trade-entry-grid-filters'),
  entities: createGridFilterStorage('entities-grid-filters'),
  entityContacts: createGridFilterStorage('entity-contacts-grid-filters'),
  entityPositions: createGridFilterStorage('entity-positions-grid-filters'),
  entityWireInstructions: createGridFilterStorage(
    'entity-wire-instructions-grid-filters',
  ),
  servicing: createGridFilterStorage('servicing-grid-filters'),
  amortizationSchedule: createGridFilterStorage(
    'amortization-schedule-grid-filters',
  ),
  facilityFees: createGridFilterStorage('facility-fees-grid-filters'),
  positionsDeal: createGridFilterStorage('positions-deal-grid-filters'),
  positionsFacility: createGridFilterStorage('positions-facility-grid-filters'),
  positionsEntity: createGridFilterStorage('positions-entity-grid-filters'),
}

/**
 * Custom hook for managing grid filters with localStorage persistence
 * @param filterHelpers - The filter storage helpers object
 * @returns Object with filters state and update handlers
 *
 * @example
 * const { filters, setDateRange, clearFilters, toMRTColumnFilters } = useGridFilters(GridFilterStorage.deals)
 *
 * // Set date range
 * setDateRange('startDate', '2024-01-01', '2024-12-31')
 *
 * // Access filters
 * const { startDate, endDate } = filters.startDate || {}
 *
 * // Pass to MainTable
 * <MainTable initialColumnFilters={toMRTColumnFilters()} />
 */
export function useGridFilters(
  filterHelpers: ReturnType<typeof createGridFilterStorage>,
) {
  const {
    loadFiltersFromStorage,
    saveFiltersToStorage,
    clearFiltersFromStorage,
  } = filterHelpers
  const [filters, setFilters] = useState<GridFilters>(() =>
    loadFiltersFromStorage(),
  )

  /**
   * Set a date range filter
   * @param filterKey - Key to identify this date range (e.g., 'startDate', 'maturityDate')
   * @param startDate - Start date (ISO string or null)
   * @param endDate - End date (ISO string or null)
   */
  const setDateRange = (
    filterKey: string,
    startDate: string | null,
    endDate: string | null,
  ) => {
    const newFilters = {
      ...filters,
      [filterKey]: { startDate, endDate },
    }
    setFilters(newFilters)
    saveFiltersToStorage(newFilters)
  }

  /**
   * Convert filters to MRT column filters format
   * Note: MRT handles date filtering internally when you set column filters
   */
  const toMRTColumnFilters = () => {
    const mrtFilters: Array<{ id: string; value: any }> = []

    Object.keys(filters).forEach(key => {
      const filter = filters[key]
      // Store the filter value as-is
      if (filter !== null && filter !== undefined) {
        mrtFilters.push({
          id: key,
          value: filter,
        })
      }
    })

    return mrtFilters
  }

  /**
   * Handle column filter changes from MRT
   * Extracts date range filters and saves them
   * Supports both direct values and updater functions
   */
  const handleMRTFiltersChange = (updaterOrValue: ColumnFiltersUpdater) => {
    const currentFilters = toMRTColumnFilters()
    const mrtFilters =
      typeof updaterOrValue === 'function'
        ? updaterOrValue(currentFilters)
        : updaterOrValue

    const newFilters: GridFilters = {}

    mrtFilters.forEach(filter => {
      // Store any filter value, not just arrays
      newFilters[filter.id] = filter.value
    })

    setFilters(newFilters)
    saveFiltersToStorage(newFilters)
  }

  /**
   * Clear all filters
   */
  const clearFilters = () => {
    setFilters({})
    clearFiltersFromStorage()
  }

  /**
   * Clear a specific filter
   */
  const clearFilter = (filterKey: string) => {
    const newFilters = { ...filters }
    delete newFilters[filterKey]
    setFilters(newFilters)
    saveFiltersToStorage(newFilters)
  }

  return {
    filters,
    setDateRange,
    toMRTColumnFilters,
    handleMRTFiltersChange,
    clearFilters,
    clearFilter,
  }
}

/**
 * Custom hook for managing grid sorting state with localStorage persistence
 * @param storageHelpers - The storage helpers object (from GridSortingStorage or createGridSortingStorage)
 * @param debug - Whether to log sorting changes to console (default: false)
 * @returns Object with sorting state and change handler
 *
 * @example
 * const { sorting, handleSortingChange } = useGridSorting(GridSortingStorage.deals)
 *
 * <MainTable
 *   initialSorting={[{ id: sorting.sortBy, desc: sorting.order === 'desc' }]}
 *   onSortingChange={handleSortingChange}
 * />
 */
export function useGridSorting(
  storageHelpers: ReturnType<typeof createGridSortingStorage>,
  debug: boolean = false,
) {
  const { loadSortFromStorage, saveSortToStorage } = storageHelpers
  const [sorting, setSorting] = useState<SortingPreference>(() =>
    loadSortFromStorage(),
  )

  /**
   * Handle sorting change from MantineReactTable
   * Supports both direct values and updater functions
   */
  const handleSortingChange = (updaterOrValue: SortingUpdater) => {
    const newSorting =
      typeof updaterOrValue === 'function'
        ? updaterOrValue([
            { id: sorting.sortBy, desc: sorting.order === 'desc' },
          ])
        : updaterOrValue

    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0]
      const newSort: SortingPreference = {
        sortBy: id,
        order: desc ? 'desc' : 'asc',
      }
      setSorting(newSort)
      saveSortToStorage(newSort.sortBy, newSort.order)

      if (debug) {
        console.log('Sorting saved to localStorage:', newSort)
      }
    }
  }

  return {
    sorting,
    handleSortingChange,
  }
}

/**
 * Custom hook for managing grid pagination state with localStorage persistence
 * @param storageHelpers - The storage helpers object (from GridPaginationStorage or createGridPaginationStorage)
 * @returns Object with pagination state and change handler
 *
 * @example
 * const { pagination, handlePaginationChange } = useGridPagination(GridPaginationStorage.deals)
 *
 * <MainTable
 *   initialPagination={{ pageSize: pagination.pageSize, pageIndex: pagination.pageIndex }}
 *   onPaginationChange={handlePaginationChange}
 * />
 */
export function useGridPagination(
  storageHelpers: ReturnType<typeof createGridPaginationStorage>,
) {
  const { loadPaginationFromStorage, savePaginationToStorage } = storageHelpers
  const [pagination, setPagination] = useState<PaginationPreference>(() =>
    loadPaginationFromStorage(),
  )

  /**
   * Handle pagination change from MantineReactTable
   * Supports both direct values and updater functions
   */
  const handlePaginationChange = (updaterOrValue: PaginationUpdater) => {
    const newPagination =
      typeof updaterOrValue === 'function'
        ? updaterOrValue(pagination)
        : updaterOrValue

    setPagination(newPagination)
    savePaginationToStorage(newPagination)
  }

  return {
    pagination,
    handlePaginationChange,
  }
}

/**
 * Custom hook for managing grid column visibility state with localStorage persistence
 * @param storageHelpers - The storage helpers object (from GridColumnVisibilityStorage or createGridColumnVisibilityStorage)
 * @param hiddenColumns - Array of column IDs that should always be hidden (e.g., systemId)
 * @returns Object with column visibility state and change handler
 *
 * @example
 * const { columnVisibility, handleColumnVisibilityChange } = useGridColumnVisibility(
 *   GridColumnVisibilityStorage.deals,
 *   ['systemId']
 * )
 *
 * <MainTable
 *   initialColumnVisibility={columnVisibility}
 *   onColumnVisibilityChange={handleColumnVisibilityChange}
 * />
 */
export function useGridColumnVisibility(
  storageHelpers: ReturnType<typeof createGridColumnVisibilityStorage>,
  hiddenColumns: string[] = [],
) {
  const { loadColumnVisibilityFromStorage, saveColumnVisibilityToStorage } =
    storageHelpers

  // Merge user preferences with permanently hidden columns
  const [columnVisibility, setColumnVisibility] =
    useState<MRT_ColumnVisibilityState>(() => {
      const userPreferences = loadColumnVisibilityFromStorage()
      const permanentlyHidden = hiddenColumns.reduce(
        (acc, colKey) => {
          acc[colKey] = false
          return acc
        },
        {} as Record<string, boolean>,
      )
      return { ...userPreferences, ...permanentlyHidden }
    })

  /**
   * Handle column visibility change from MantineReactTable
   * Supports both direct values and updater functions
   */
  const handleColumnVisibilityChange = (
    updaterOrValue: ColumnVisibilityUpdater,
  ) => {
    setColumnVisibility(oldVisibility => {
      const newVisibility =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(oldVisibility)
          : updaterOrValue

      // Filter out permanently hidden columns before saving to localStorage
      const visibilityToSave = Object.keys(newVisibility).reduce((acc, key) => {
        if (!hiddenColumns.includes(key)) {
          acc[key] = newVisibility[key]
        }
        return acc
      }, {} as MRT_ColumnVisibilityState)

      saveColumnVisibilityToStorage(visibilityToSave)
      return newVisibility
    })
  }

  return {
    columnVisibility,
    handleColumnVisibilityChange,
  }
}

/**
 * Trade Entry Filter Types and Storage
 */
export interface TradeFilterValues {
  tradeStatus?: string[]
  tradeDate?: string | Date[] | null[]
  settlementDate?: string | Date[] | null[]
  tradeAction?: string[]
  deal?: string[]
  /**
   * Matches an entity on either SIDE of the trade — buyer or seller of the master
   * trade or its sub-trades. Supersedes myEntity and counterparty; the agent is not
   * a side, so the Agent filter stays independent.
   */
  entityAnySide?: string[]
  myEntity?: string[]
  counterparty?: string[]
  agent?: string[]
  paymentMethod?: string[]
  aaStatus?: string[]
  unappliedTransactions?: string
  /** 'Locked' | 'Unlocked' — an active LendOS-placed hold on the trade. */
  lockStatus?: string
  /** 'On Hold' | 'Not On Hold' — an active Signing-Portal-placed (Agent) hold. */
  holdStatus?: string
  allocated?: string
  /** 'Actioned' | 'Unactioned' — actioned state of a trade's Activity Stream notes. */
  activityStream?: string
  /** 'Stale' | 'Not Stale' — staleness of the trade's sent Open delivery packet. */
  staleDocuments?: string
  documentsNotRefreshed?: string
}

const TRADE_FILTERS_STORAGE_KEY = 'trade-entry-filters'

export const SINGLE_VALUE_FILTER_KEYS: ReadonlyArray<keyof TradeFilterValues> =
  [
    'unappliedTransactions',
    'lockStatus',
    'holdStatus',
    'allocated',
    'activityStream',
    'staleDocuments',
    'documentsNotRefreshed',
  ]

const DATE_FILTER_KEYS: ReadonlyArray<keyof TradeFilterValues> = [
  'tradeDate',
  'settlementDate',
]

/**
 * Load trade filters from localStorage
 * @returns Saved trade filter values or default values
 */
export function loadTradeFiltersFromStorage(): TradeFilterValues {
  try {
    const stored = localStorage.getItem(TRADE_FILTERS_STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      // Convert date string arrays back to Date objects if they exist
      if (parsed.tradeDate && Array.isArray(parsed.tradeDate)) {
        parsed.tradeDate = parsed.tradeDate.map((date: string) =>
          date ? new Date(date) : null,
        )
      }
      if (parsed.settlementDate && Array.isArray(parsed.settlementDate)) {
        parsed.settlementDate = parsed.settlementDate.map((date: string) =>
          date ? new Date(date) : null,
        )
      }
      for (const key of Object.keys(parsed) as Array<keyof TradeFilterValues>) {
        if (SINGLE_VALUE_FILTER_KEYS.includes(key)) continue
        if (DATE_FILTER_KEYS.includes(key)) continue
        const v = parsed[key]
        if (typeof v === 'string') {
          parsed[key] = v ? [v] : []
        }
      }
      return parsed
    }
  } catch (error) {
    console.error('Error loading trade filters from localStorage:', error)
  }
  return { tradeStatus: ['Open'] } // Default to 'Open' status
}

/**
 * Save trade filters to localStorage
 * @param filters - Trade filter values to save
 */
export function saveTradeFiltersToStorage(filters: TradeFilterValues): void {
  try {
    localStorage.setItem(TRADE_FILTERS_STORAGE_KEY, JSON.stringify(filters))
  } catch (error) {
    console.error('Error saving trade filters to localStorage:', error)
  }
}

/**
 * Clear trade filters from localStorage
 */
export function clearTradeFiltersFromStorage(): void {
  try {
    localStorage.removeItem(TRADE_FILTERS_STORAGE_KEY)
  } catch (error) {
    console.error('Error clearing trade filters from localStorage:', error)
  }
}

/**
 * Trade Entry Sort Preferences
 */
export interface TradeSortPreference {
  sortBy: string
  order: 'Ascending' | 'Descending'
}

const TRADE_SORT_STORAGE_KEY = 'trade-entry-sort'

/**
 * Load trade sort preferences from localStorage
 * @returns Saved trade sort preferences or default values
 */
export function loadTradeSortFromStorage(): TradeSortPreference {
  try {
    const stored = localStorage.getItem(TRADE_SORT_STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (error) {
    console.error('Error loading trade sort from localStorage:', error)
  }
  return { sortBy: 'tradeDate', order: 'Ascending' } // Default values
}

/**
 * Save trade sort preferences to localStorage
 * @param sortPreference - Trade sort preferences to save
 */
export function saveTradeSortToStorage(
  sortPreference: TradeSortPreference,
): void {
  try {
    localStorage.setItem(TRADE_SORT_STORAGE_KEY, JSON.stringify(sortPreference))
  } catch (error) {
    console.error('Error saving trade sort to localStorage:', error)
  }
}

/**
 * Clear trade sort preferences from localStorage
 */
export function clearTradeSortFromStorage(): void {
  try {
    localStorage.removeItem(TRADE_SORT_STORAGE_KEY)
  } catch (error) {
    console.error('Error clearing trade sort from localStorage:', error)
  }
}

/**
 * Facility History Filter / Sort Storage
 */
export interface FacilityHistoryFilterValues {
  level?: string
  transactionType?: string
  source?: string
}

const FACILITY_HISTORY_FILTERS_KEY = 'facility-history-filters'
const FACILITY_HISTORY_SORT_KEY = 'facility-history-sort'

export interface FacilityHistorySortPreference {
  sortBy: string
  order: 'Ascending' | 'Descending'
}

export function loadFacilityHistoryFiltersFromStorage(): FacilityHistoryFilterValues {
  try {
    const stored = localStorage.getItem(FACILITY_HISTORY_FILTERS_KEY)
    if (stored) return JSON.parse(stored)
  } catch (error) {
    console.error('Error loading facility history filters:', error)
  }
  return {}
}

export function saveFacilityHistoryFiltersToStorage(
  filters: FacilityHistoryFilterValues,
): void {
  try {
    localStorage.setItem(FACILITY_HISTORY_FILTERS_KEY, JSON.stringify(filters))
  } catch (error) {
    console.error('Error saving facility history filters:', error)
  }
}

export function loadFacilityHistorySortFromStorage(): FacilityHistorySortPreference {
  try {
    const stored = localStorage.getItem(FACILITY_HISTORY_SORT_KEY)
    if (stored) return JSON.parse(stored)
  } catch (error) {
    console.error('Error loading facility history sort:', error)
  }
  return { sortBy: 'processedAt', order: 'Ascending' }
}

export function saveFacilityHistorySortToStorage(
  pref: FacilityHistorySortPreference,
): void {
  try {
    localStorage.setItem(FACILITY_HISTORY_SORT_KEY, JSON.stringify(pref))
  } catch (error) {
    console.error('Error saving facility history sort:', error)
  }
}

/**
 * Entity Search State Storage
 */
const ENTITY_SEARCH_STORAGE_KEY = 'entity-search-value'

/**
 * Load entity search value from localStorage
 * @returns Saved search value or empty string
 */
export function loadEntitySearchFromStorage(): string {
  try {
    const stored = localStorage.getItem(ENTITY_SEARCH_STORAGE_KEY)
    if (stored) {
      return stored
    }
  } catch (error) {
    console.error('Error loading entity search from localStorage:', error)
  }
  return ''
}

/**
 * Save entity search value to localStorage
 * @param searchValue - Search value to save
 */
export function saveEntitySearchToStorage(searchValue: string): void {
  try {
    if (searchValue) {
      localStorage.setItem(ENTITY_SEARCH_STORAGE_KEY, searchValue)
    } else {
      localStorage.removeItem(ENTITY_SEARCH_STORAGE_KEY)
    }
  } catch (error) {
    console.error('Error saving entity search to localStorage:', error)
  }
}

/**
 * Clear entity search value from localStorage
 */
export function clearEntitySearchFromStorage(): void {
  try {
    localStorage.removeItem(ENTITY_SEARCH_STORAGE_KEY)
  } catch (error) {
    console.error('Error clearing entity search from localStorage:', error)
  }
}

/**
 * Trade Search State Storage
 */
const TRADE_SEARCH_STORAGE_KEY = 'trade-search-value'

/**
 * Load trade search value from localStorage
 * @returns Saved search value or empty string
 */
export function loadTradeSearchFromStorage(): string {
  try {
    const stored = localStorage.getItem(TRADE_SEARCH_STORAGE_KEY)
    if (stored) {
      return stored
    }
  } catch (error) {
    console.error('Error loading trade search from localStorage:', error)
  }
  return ''
}

/**
 * Save trade search value to localStorage
 * @param searchValue - Search value to save
 */
export function saveTradeSearchToStorage(searchValue: string): void {
  try {
    if (searchValue) {
      localStorage.setItem(TRADE_SEARCH_STORAGE_KEY, searchValue)
    } else {
      localStorage.removeItem(TRADE_SEARCH_STORAGE_KEY)
    }
  } catch (error) {
    console.error('Error saving trade search to localStorage:', error)
  }
}

/**
 * Clear trade search value from localStorage
 */
export function clearTradeSearchFromStorage(): void {
  try {
    localStorage.removeItem(TRADE_SEARCH_STORAGE_KEY)
  } catch (error) {
    console.error('Error clearing trade search from localStorage:', error)
  }
}

/**
 * Clear all grid-related localStorage on logout
 * This clears sorting, pagination, column visibility, filters, and search values for all grids
 */
export function clearAllGridStorageOnLogout(): void {
  // Clear all sorting preferences
  Object.values(GridSortingStorage).forEach(storage =>
    storage.clearSortFromStorage(),
  )

  // Clear all pagination preferences
  Object.values(GridPaginationStorage).forEach(storage =>
    storage.clearPaginationFromStorage(),
  )

  // Clear all column visibility preferences
  Object.values(GridColumnVisibilityStorage).forEach(storage =>
    storage.clearColumnVisibilityFromStorage(),
  )

  // Clear all filter preferences
  Object.values(GridFilterStorage).forEach(storage =>
    storage.clearFiltersFromStorage(),
  )

  // Clear trade-specific storage
  clearTradeFiltersFromStorage()
  clearTradeSortFromStorage()

  // Clear search values
  clearEntitySearchFromStorage()
  clearTradeSearchFromStorage()
}
